import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { FiberManualRecord, FolderOpen, Settings } from '@mui/icons-material';
import { Camera } from '../types';
import { getThumbnailUrl, startManualEvent, endManualEvent } from '../frigateApi';
import { useLanguage } from '../i18n/LanguageContext';
import { CameraSettingsDialog } from './CameraSettingsDialog';

const THUMB_W = 96;
const POLL_MS = 3000;

interface RowProps {
  camera: Camera;
  gatewayUrl: string;
  selected: boolean;
  recordDuration: number;
  onSelect: () => void;
  onOpenRecordings: () => void;
  onOpenSettings: () => void;
}

const CameraRow: React.FC<RowProps> = ({
  camera,
  gatewayUrl,
  selected,
  recordDuration,
  onSelect,
  onOpenRecordings,
  onOpenSettings,
}) => {
  const { t } = useLanguage();
  const [src, setSrc] = useState('');
  const thumbTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [recLoading, setRecLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recError, setRecError] = useState(false);
  const [recDone, setRecDone] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const eventIdRef = useRef<string | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const gatewayUrlRef = useRef(gatewayUrl);
  useEffect(() => { gatewayUrlRef.current = gatewayUrl; }, [gatewayUrl]);

  useEffect(() => {
    const refresh = () => setSrc(`${getThumbnailUrl(gatewayUrl, camera.name)}?t=${Date.now()}`);
    refresh();
    thumbTimerRef.current = setInterval(refresh, POLL_MS);
    return () => { if (thumbTimerRef.current) clearInterval(thumbTimerRef.current); };
  }, [gatewayUrl, camera.name]);

  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (eventIdRef.current) {
        endManualEvent(gatewayUrlRef.current, eventIdRef.current).catch(() => {});
      }
    };
  }, []);

  const handleRec = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (recording || recLoading) return;
    setRecLoading(true);
    try {
      const eventId = await startManualEvent(gatewayUrl, camera.name);
      eventIdRef.current = eventId;
      setRecording(true);
      setCountdown(recordDuration);
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current!);
            endManualEvent(gatewayUrlRef.current, eventIdRef.current!).catch(() => {});
            eventIdRef.current = null;
            setRecording(false);
            setRecDone(true);
            setTimeout(() => setRecDone(false), 2000);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch {
      setRecError(true);
      setTimeout(() => setRecError(false), 2000);
    } finally {
      setRecLoading(false);
    }
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={t.a11yShowCamera(camera.name)}
      aria-pressed={selected}
      onClick={onSelect}
      onKeyDown={e => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1,
        cursor: 'pointer',
        borderRadius: 1,
        bgcolor: selected ? 'action.selected' : 'transparent',
        outline: selected ? '2px solid' : '2px solid transparent',
        outlineColor: selected ? 'primary.main' : 'transparent',
        transition: 'background-color 0.15s, outline-color 0.15s',
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
      }}
    >
      <Box
        sx={{
          width: THUMB_W,
          height: Math.round(THUMB_W * 9 / 16),
          flexShrink: 0,
          bgcolor: 'black',
          borderRadius: 0.5,
          overflow: 'hidden',
        }}
      >
        {src && (
          <img
            src={src}
            alt={camera.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
      </Box>

      <Typography variant="body2" sx={{ flex: 1, fontWeight: selected ? 600 : 400 }}>
        {camera.name}
      </Typography>

      <Box onClick={e => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {recLoading ? (
          <CircularProgress size={20} />
        ) : (
          <>
            <Tooltip title={recError ? t.errorConnection : recording ? `${t.cameraRecording} (${countdown}s)` : t.cameraRecord}>
              <IconButton
                size="small"
                onClick={handleRec}
                sx={{
                  color: recError ? 'warning.main' : recDone ? 'success.main' : recording ? 'error.main' : 'error.light',
                  gap: 0.3,
                  '@keyframes recPulse': {
                    '0%':   { opacity: 1 },
                    '50%':  { opacity: 0.35 },
                    '100%': { opacity: 1 },
                  },
                  animation: recording ? 'recPulse 1s ease-in-out infinite' : 'none',
                }}
              >
                <FiberManualRecord fontSize="small" />
                <Typography variant="caption" sx={{ fontSize: '10px', fontWeight: 700, lineHeight: 1 }}>
                  {recError ? 'ERR' : recDone ? 'OK' : 'REC'}
                </Typography>
              </IconButton>
            </Tooltip>
          </>
        )}
        <Tooltip title={t.cameraRecordings}>
          <IconButton
            size="small"
            onClick={e => { e.stopPropagation(); onOpenRecordings(); }}
          >
            <FolderOpen fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t.cameraSettings}>
          <IconButton
            size="small"
            onClick={e => { e.stopPropagation(); onOpenSettings(); }}
          >
            <Settings fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

interface Props {
  cameras: Camera[];
  selected: Camera | null;
  gatewayUrl: string;
  recordDuration: number;
  onSelect: (camera: Camera) => void;
  onOpenRecordings: (camera: Camera) => void;
  onUpdate: (camera: Camera) => void;
  onRecordDurationChange: (val: number) => void;
}

export const CameraGrid: React.FC<Props> = ({
  cameras,
  selected,
  gatewayUrl,
  recordDuration,
  onSelect,
  onOpenRecordings,
  onUpdate,
  onRecordDurationChange,
}) => {
  const [settingsCamera, setSettingsCamera] = useState<Camera | null>(null);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {cameras.map(cam => (
          <CameraRow
            key={cam.name}
            camera={cam}
            gatewayUrl={gatewayUrl}
            selected={selected?.name === cam.name}
            recordDuration={recordDuration}
            onSelect={() => onSelect(cam)}
            onOpenRecordings={() => onOpenRecordings(cam)}
            onOpenSettings={() => setSettingsCamera(cam)}
          />
        ))}
      </Box>

      <CameraSettingsDialog
        camera={settingsCamera}
        gatewayUrl={gatewayUrl}
        recordDuration={recordDuration}
        onRecordDurationChange={onRecordDurationChange}
        open={settingsCamera !== null}
        onClose={() => setSettingsCamera(null)}
        onUpdate={cam => {
          onUpdate(cam);
          setSettingsCamera(cam);
        }}
      />
    </>
  );
};
